import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Table as TableIcon,
  LayoutGrid,
  Search,
  X,
  Download,
  Pencil,
  Calendar as CalendarIcon,
  BarChart3,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { OperationsTable } from './OperationsTable';
import { OperationsCards } from './OperationsCards';
import { CalendarView } from './CalendarView';
import { ChartView } from './ChartView';
import { ConditionalFormatDialog } from './ConditionalFormatDialog';
import { SavedViewPicker } from './SavedViewPicker';
import { DisplayOptions } from './DisplayOptions';
import { processRows } from './processRows';
import { GroupMode, OperationsViewState, useSavedViews } from './savedViews';
import {
  CalendarConfig,
  ChartConfig,
  ColumnDef,
  ColumnId,
  ConditionalFormatRule,
  DateFilter,
  DateFilterState,
  DisplayOptions as DisplayOptionsState,
  FilterState,
  FormatsState,
  SortState,
  Stage,
  TotalsState,
  ViewMode,
} from './types';

const DEFAULT_DISPLAY: DisplayOptionsState = {
  rowSpacing: 'tight',
  zebra: false,
  hiddenColumns: new Set(),
};

function defaultHiddenColumns<T>(columns: ColumnDef<T>[]): Set<string> {
  return new Set(columns.filter((c) => c.defaultHidden).map((c) => c.id));
}

function applyColumnOrder<T>(
  columns: ColumnDef<T>[],
  order: ColumnId[] | undefined
): ColumnDef<T>[] {
  if (!order || order.length === 0) return columns;
  const byId = new Map(columns.map((c) => [c.id, c]));
  const out: ColumnDef<T>[] = [];
  const seen = new Set<string>();
  for (const id of order) {
    const c = byId.get(id);
    if (c && !seen.has(id)) {
      out.push(c);
      seen.add(id);
    }
  }
  for (const c of columns) {
    if (!seen.has(c.id)) out.push(c);
  }
  return out;
}

function defaultFiltersFromColumns<T>(columns: ColumnDef<T>[]): FilterState {
  const out: FilterState = {};
  for (const c of columns) {
    if (c.defaultFilter && c.defaultFilter.length > 0) {
      out[c.id] = new Set(c.defaultFilter);
    }
  }
  return out;
}

interface OperationsViewProps<T extends { id: string }> {
  columns: ColumnDef<T>[];
  useData: () => T[];
  savedViewsEntityKey: string;
  defaultSort?: SortState;
  titleColumnId?: string;
  stageAccessor?: (row: T) => Stage | undefined;
  entityLabel?: string;
  showSavedViewPicker?: boolean;
  initialViewId?: string;
  onRowClick?: (row: T) => void;
}

export function OperationsView<T extends { id: string }>({
  columns,
  useData,
  savedViewsEntityKey,
  defaultSort,
  titleColumnId,
  stageAccessor,
  entityLabel = 'records',
  showSavedViewPicker = false,
  initialViewId,
  onRowClick,
}: OperationsViewProps<T>) {
  const allRows = useData();
  const { views, loading: viewsLoading, createView, updateView, deleteView, currentUserId } =
    useSavedViews(savedViewsEntityKey);

  const DEFAULT_STATE: OperationsViewState = useMemo(
    () => ({
      sort: defaultSort ?? [],
      filters: defaultFiltersFromColumns(columns),
      dateFilters: {},
      totals: new Set(),
      formats: {},
      viewMode: 'table',
      display: {
        ...DEFAULT_DISPLAY,
        hiddenColumns: defaultHiddenColumns(columns),
      },
      groupBy: undefined,
      groupMode: 'detail',
      calendarConfig: undefined,
      chartConfig: undefined,
      columnOrder: undefined,
      columnLabels: undefined,
      columnWidths: undefined,
    }),
    [defaultSort, columns]
  );

  const [viewMode, setViewMode] = useState<ViewMode>(DEFAULT_STATE.viewMode);
  const [sort, setSort] = useState<SortState>(DEFAULT_STATE.sort);
  const [filters, setFilters] = useState<FilterState>(() => defaultFiltersFromColumns(columns));
  const [dateFilters, setDateFilters] = useState<DateFilterState>({});
  const [totals, setTotals] = useState<TotalsState>(DEFAULT_STATE.totals);
  const [formats, setFormats] = useState<FormatsState>(DEFAULT_STATE.formats);
  const [display, setDisplay] = useState<DisplayOptionsState>(() => ({
    ...DEFAULT_DISPLAY,
    hiddenColumns: defaultHiddenColumns(columns),
  }));
  const [groupBy, setGroupBy] = useState<ColumnId | undefined>(undefined);
  const [groupMode, setGroupMode] = useState<GroupMode>('detail');
  const [calendarConfig, setCalendarConfig] = useState<CalendarConfig | undefined>(undefined);
  const [chartConfig, setChartConfig] = useState<ChartConfig | undefined>(undefined);
  const [columnOrder, setColumnOrder] = useState<ColumnId[] | undefined>(undefined);
  const [columnLabels, setColumnLabels] = useState<Record<ColumnId, string> | undefined>(undefined);
  const [columnWidths, setColumnWidths] = useState<Record<ColumnId, number> | undefined>(undefined);

  // Reorder + relabel columns ONCE; everything downstream (table, headers,
  // gear, chart axes, calendar pills, etc.) sees the user-arranged version.
  const effectiveColumns = useMemo(() => {
    const ordered = applyColumnOrder(columns, columnOrder);
    if (!columnLabels) return ordered;
    return ordered.map((c) =>
      columnLabels[c.id] ? { ...c, label: columnLabels[c.id] } : c
    );
  }, [columns, columnOrder, columnLabels]);
  const [formatColumn, setFormatColumn] = useState<ColumnId | null>(null);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const currentState: OperationsViewState = {
    viewMode,
    sort,
    filters,
    dateFilters,
    totals,
    formats,
    display,
    groupBy,
    groupMode,
    calendarConfig,
    chartConfig,
    columnOrder,
    columnLabels,
    columnWidths,
  };

  const activeView = views.find((v) => v.id === activeViewId) ?? null;
  const dirty = activeView
    ? !statesEqual(currentState, activeView.state)
    : !statesEqual(currentState, DEFAULT_STATE);

  const applyState = (s: OperationsViewState) => {
    setViewMode(s.viewMode);
    setSort(s.sort);
    setFilters(s.filters);
    setDateFilters(s.dateFilters ?? {});
    setTotals(new Set(s.totals));
    setFormats(s.formats);
    setDisplay({
      rowSpacing: s.display.rowSpacing,
      zebra: s.display.zebra,
      hiddenColumns: new Set(s.display.hiddenColumns),
    });
    setGroupBy(s.groupBy);
    setGroupMode(s.groupMode ?? 'detail');
    setCalendarConfig(s.calendarConfig);
    setChartConfig(s.chartConfig);
    setColumnOrder(s.columnOrder);
    setColumnLabels(s.columnLabels);
    setColumnWidths(s.columnWidths);
  };

  const handleSelectView = (id: string) => {
    const v = views.find((x) => x.id === id);
    if (!v) return;
    setActiveViewId(id);
    applyState(v.state);
  };

  const handleCreateView = async (name: string, isShared: boolean) => {
    try {
      const view = await createView(name, currentState, isShared);
      setActiveViewId(view.id);
    } catch (err) {
      console.error('Failed to create view', err);
    }
  };

  const handleUpdateView = async (id: string) => {
    try {
      await updateView(id, { state: currentState });
    } catch (err) {
      console.error('Failed to update view', err);
    }
  };

  const handleDeleteView = async (id: string) => {
    try {
      await deleteView(id);
      if (activeViewId === id) setActiveViewId(null);
    } catch (err) {
      console.error('Failed to delete view', err);
    }
  };

  const handleClearView = () => {
    // "Clear" falls back to the system Default for this entity if one exists,
    // otherwise to the column-registry defaults. Either way, the user gets the
    // out-of-the-box starting state, not an arbitrary previous view.
    const sysDefault = views.find((v) => v.isSystem && v.name === 'Default');
    if (sysDefault) {
      setActiveViewId(sysDefault.id);
      applyState(sysDefault.state);
    } else {
      setActiveViewId(null);
      applyState(DEFAULT_STATE);
    }
  };

  // On first mount: apply the explicit `initialViewId` if supplied (deep-link
  // from Saved Views page), else auto-apply the system "Default" view for this
  // entity, else fall through to the column-registry default state.
  const appliedInitialRef = useRef(false);
  useEffect(() => {
    if (appliedInitialRef.current) return;
    if (viewsLoading) return;

    let target = null;
    if (initialViewId) {
      target = views.find((v) => v.id === initialViewId) ?? null;
    }
    if (!target) {
      target = views.find((v) => v.isSystem && v.name === 'Default') ?? null;
    }
    appliedInitialRef.current = true;
    if (target) {
      setActiveViewId(target.id);
      applyState(target.state);
    }
  }, [initialViewId, views, viewsLoading]);

  const searchedRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter((row) => columns.some((col) => col.accessor(row).toLowerCase().includes(q)));
  }, [allRows, search, columns]);

  const processedRows = useMemo(
    () => processRows(searchedRows, columns, filters, sort, dateFilters),
    [searchedRows, columns, filters, sort, dateFilters]
  );

  const handleSort = (
    columnId: ColumnId,
    action: 'replace-asc' | 'replace-desc' | 'append-asc' | 'append-desc' | 'remove'
  ) => {
    setSort((prev) => {
      switch (action) {
        case 'replace-asc':
          return [{ columnId, direction: 'asc' }];
        case 'replace-desc':
          return [{ columnId, direction: 'desc' }];
        case 'append-asc':
          return [...prev.filter((s) => s.columnId !== columnId), { columnId, direction: 'asc' }];
        case 'append-desc':
          return [...prev.filter((s) => s.columnId !== columnId), { columnId, direction: 'desc' }];
        case 'remove':
          return prev.filter((s) => s.columnId !== columnId);
      }
    });
  };

  const handleFilterChange = (columnId: ColumnId, values: Set<string>) =>
    setFilters((prev) => ({ ...prev, [columnId]: values }));

  const handleDateFilterChange = (columnId: ColumnId, df: DateFilter | null) =>
    setDateFilters((prev) => {
      const next = { ...prev };
      if (df) next[columnId] = df;
      else delete next[columnId];
      return next;
    });

  const handleToggleTotal = (columnId: ColumnId) =>
    setTotals((prev) => {
      const next = new Set(prev);
      if (next.has(columnId)) next.delete(columnId);
      else next.add(columnId);
      return next;
    });

  const handleApplyFormat = (columnId: ColumnId, rules: ConditionalFormatRule[]) =>
    setFormats((prev) => {
      const next = { ...prev };
      if (rules.length === 0) delete next[columnId];
      else next[columnId] = rules;
      return next;
    });

  const handleRemoveFormat = (columnId: ColumnId) =>
    setFormats((prev) => {
      const next = { ...prev };
      delete next[columnId];
      return next;
    });

  const columnById = useMemo(() => {
    const m = new Map<string, ColumnDef<T>>();
    for (const c of columns) m.set(c.id, c);
    return m;
  }, [columns]);

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-3 space-y-2">
      <div className="flex items-center gap-2">
        <SearchBox value={search} onChange={setSearch} entityLabel={entityLabel} />

        {showSavedViewPicker && (
          <SavedViewPicker
            views={views}
            activeViewId={activeViewId}
            currentState={currentState}
            currentUserId={currentUserId}
            dirty={dirty}
            onSelect={handleSelectView}
            onCreate={handleCreateView}
            onUpdate={handleUpdateView}
            onDelete={handleDeleteView}
            onClear={handleClearView}
          />
        )}

        <div className="ml-auto flex items-center gap-2">
          <InlineEditToggle />
          <div className="inline-flex items-center border border-slate-200 bg-white rounded-md p-0.5 h-9">
            <ViewToggleButton
              active={viewMode === 'table'}
              onClick={() => setViewMode('table')}
              icon={<TableIcon className="w-4 h-4" />}
              label="Table view"
            />
            <ViewToggleButton
              active={viewMode === 'cards'}
              onClick={() => setViewMode('cards')}
              icon={<LayoutGrid className="w-4 h-4" />}
              label="Card view"
            />
            <ViewToggleButton
              active={viewMode === 'calendar'}
              onClick={() => setViewMode('calendar')}
              icon={<CalendarIcon className="w-4 h-4" />}
              label="Calendar view"
            />
            <ViewToggleButton
              active={viewMode === 'chart'}
              onClick={() => setViewMode('chart')}
              icon={<BarChart3 className="w-4 h-4" />}
              label="Chart view"
            />
          </div>
          <DisplayOptions
            value={display}
            columns={effectiveColumns}
            viewMode={viewMode}
            calendarConfig={calendarConfig}
            chartConfig={chartConfig}
            columnOrder={columnOrder}
            filters={filters}
            dateFilters={dateFilters}
            sort={sort}
            formats={formats}
            groupBy={groupBy}
            onChange={setDisplay}
            onCalendarChange={setCalendarConfig}
            onChartChange={setChartConfig}
            onReorder={setColumnOrder}
          />
          <button
            onClick={() => exportCsv(processedRows, effectiveColumns, display.hiddenColumns, entityLabel)}
            className="flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-slate-600"
            title="Export to CSV"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      {viewMode === 'table' && (
        <OperationsTable
          rows={processedRows}
          allRows={searchedRows}
          columns={effectiveColumns}
          sort={sort}
          filters={filters}
          dateFilters={dateFilters}
          totals={totals}
          formats={formats}
          display={display}
          groupBy={groupBy}
          groupMode={groupMode}
          onSort={handleSort}
          onFilterChange={handleFilterChange}
          onDateFilterChange={handleDateFilterChange}
          onToggleTotal={handleToggleTotal}
          onOpenFormat={(colId) => setFormatColumn(colId)}
          onSetGroupBy={(colId) => setGroupBy(colId)}
          onSetGroupMode={setGroupMode}
          onRenameColumn={(colId, label) =>
            setColumnLabels((prev) => {
              const next = { ...(prev ?? {}) };
              if (label) next[colId] = label;
              else delete next[colId];
              return Object.keys(next).length === 0 ? undefined : next;
            })
          }
          columnWidths={columnWidths}
          onResizeColumn={(colId, width) =>
            setColumnWidths((prev) => {
              const next = { ...(prev ?? {}) };
              if (width === undefined) delete next[colId];
              else next[colId] = width;
              return Object.keys(next).length === 0 ? undefined : next;
            })
          }
          onRowClick={onRowClick}
        />
      )}
      {viewMode === 'cards' && (
        <OperationsCards
          rows={processedRows}
          columns={effectiveColumns}
          formats={formats}
          display={display}
          titleColumnId={titleColumnId}
          stageAccessor={stageAccessor}
        />
      )}
      {viewMode === 'calendar' && (
        <CalendarView
          rows={processedRows}
          columns={effectiveColumns}
          visibleColumns={effectiveColumns.filter((c) => !display.hiddenColumns.has(c.id))}
          config={calendarConfig}
          titleColumnId={titleColumnId}
          onRowClick={onRowClick}
        />
      )}
      {viewMode === 'chart' && (
        <ChartView
          rows={processedRows}
          columns={effectiveColumns}
          formats={formats}
          config={chartConfig}
        />
      )}

      <ConditionalFormatDialog
        open={formatColumn !== null}
        column={formatColumn ? columnById.get(formatColumn) ?? null : null}
        uniqueValues={
          formatColumn && columnById.get(formatColumn)
            ? Array.from(
                new Set(searchedRows.map((r) => columnById.get(formatColumn)!.accessor(r)))
              )
                .filter(Boolean)
                .sort()
            : []
        }
        existingRules={formatColumn ? formats[formatColumn] : undefined}
        onApply={(rules) => formatColumn && handleApplyFormat(formatColumn, rules)}
        onRemove={() => formatColumn && handleRemoveFormat(formatColumn)}
        onOpenChange={(o) => {
          if (!o) setFormatColumn(null);
        }}
      />
    </div>
  );
}

function statesEqual(a: OperationsViewState, b: OperationsViewState): boolean {
  if (a.viewMode !== b.viewMode) return false;
  if (JSON.stringify(a.sort) !== JSON.stringify(b.sort)) return false;
  if (JSON.stringify(a.formats) !== JSON.stringify(b.formats)) return false;

  const aTotals = Array.from(a.totals).sort();
  const bTotals = Array.from(b.totals).sort();
  if (aTotals.length !== bTotals.length || aTotals.some((v, i) => v !== bTotals[i])) return false;

  const aFilterKeys = Object.keys(a.filters).filter((k) => (a.filters[k]?.size ?? 0) > 0);
  const bFilterKeys = Object.keys(b.filters).filter((k) => (b.filters[k]?.size ?? 0) > 0);
  if (aFilterKeys.length !== bFilterKeys.length) return false;
  for (const k of aFilterKeys) {
    const av = Array.from(a.filters[k] ?? []).sort();
    const bv = Array.from(b.filters[k] ?? []).sort();
    if (av.length !== bv.length || av.some((v, i) => v !== bv[i])) return false;
  }

  const aDate = a.dateFilters ?? {};
  const bDate = b.dateFilters ?? {};
  const aDateKeys = Object.keys(aDate).filter((k) => !!aDate[k]);
  const bDateKeys = Object.keys(bDate).filter((k) => !!bDate[k]);
  if (aDateKeys.length !== bDateKeys.length) return false;
  for (const k of aDateKeys) {
    if (JSON.stringify(aDate[k]) !== JSON.stringify(bDate[k])) return false;
  }

  if (a.display.rowSpacing !== b.display.rowSpacing) return false;
  if (a.display.zebra !== b.display.zebra) return false;
  const aHidden = Array.from(a.display.hiddenColumns).sort();
  const bHidden = Array.from(b.display.hiddenColumns).sort();
  if (aHidden.length !== bHidden.length || aHidden.some((v, i) => v !== bHidden[i])) return false;

  if ((a.groupBy ?? '') !== (b.groupBy ?? '')) return false;
  if ((a.groupMode ?? 'detail') !== (b.groupMode ?? 'detail')) return false;
  if (JSON.stringify(a.calendarConfig ?? null) !== JSON.stringify(b.calendarConfig ?? null))
    return false;
  if (JSON.stringify(a.chartConfig ?? null) !== JSON.stringify(b.chartConfig ?? null)) return false;
  if (JSON.stringify(a.columnOrder ?? null) !== JSON.stringify(b.columnOrder ?? null)) return false;
  if (JSON.stringify(a.columnLabels ?? null) !== JSON.stringify(b.columnLabels ?? null))
    return false;
  if (JSON.stringify(a.columnWidths ?? null) !== JSON.stringify(b.columnWidths ?? null))
    return false;

  return true;
}

function InlineEditToggle() {
  const enabled = false;
  return (
    <button
      type="button"
      disabled
      title="Inline editing (coming soon)"
      className="flex items-center gap-1.5 h-9 px-2 rounded-md border border-slate-200 bg-white text-slate-400 cursor-not-allowed"
    >
      <Pencil className="w-3.5 h-3.5" />
      <span
        className={cn(
          'inline-flex items-center w-8 h-4 rounded-full transition-colors',
          enabled ? 'bg-emerald-500' : 'bg-slate-200'
        )}
      >
        <span
          className={cn(
            'w-3 h-3 bg-white rounded-full shadow transition-transform',
            enabled ? 'translate-x-4' : 'translate-x-0.5'
          )}
        />
      </span>
    </button>
  );
}

function exportCsv<T>(
  rows: T[],
  columns: ColumnDef<T>[],
  hiddenColumns: Set<string>,
  entityLabel: string
) {
  const cols = columns.filter((c) => !hiddenColumns.has(c.id));
  const escape = (v: string) => `"${(v ?? '').replace(/"/g, '""')}"`;
  const header = cols.map((c) => escape(c.label)).join(',');
  const body = rows.map((r) => cols.map((c) => escape(c.accessor(r))).join(',')).join('\n');
  const csv = `${header}\n${body}\n`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${entityLabel}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

interface SearchBoxProps {
  value: string;
  onChange: (v: string) => void;
  entityLabel: string;
}

function SearchBox({ value, onChange, entityLabel }: SearchBoxProps) {
  return (
    <div className="flex-1 min-w-0 max-w-xs flex items-center gap-2 border border-slate-200 bg-white rounded-md h-9 pl-3 pr-1">
      <Search className="w-4 h-4 text-slate-400 shrink-0" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Search ${entityLabel}`}
        className="flex-1 min-w-0 text-sm outline-none bg-transparent"
      />
      {value && (
        <button
          onClick={() => onChange('')}
          className="text-slate-400 hover:text-slate-600 p-1 shrink-0"
          title="Clear"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

interface ViewToggleButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}

function ViewToggleButton({ active, onClick, icon, label }: ViewToggleButtonProps) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      className={cn(
        'flex items-center justify-center w-9 h-8 rounded-sm transition-colors',
        active ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
      )}
    >
      {icon}
    </button>
  );
}
